<script>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useAlert } from 'dashboard/composables';
import { useMetricaAiConfig } from 'dashboard/composables/useMetricaAiConfig';
import { vOnClickOutside } from '@vueuse/components';
import { REPLY_EDITOR_MODES, CHAR_LENGTH_WARNING } from './constants';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EditorModeToggle from './EditorModeToggle.vue';
import MetricaAiAssistAPI from 'dashboard/api/metrica/aiAssist';

export default {
  name: 'ReplyTopPanel',
  components: {
    NextButton,
    EditorModeToggle,
  },
  directives: {
    OnClickOutside: vOnClickOutside,
  },
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    isReplyRestricted: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      default: null,
    },
    isMessageLengthReachingThreshold: {
      type: Boolean,
      default: () => false,
    },
    charactersRemaining: {
      type: Number,
      default: () => 0,
    },
  },
  emits: ['setReplyMode', 'toggleEditorSize', 'executeCopilotAction'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const { loaded: aiConfigLoaded, capabilities: aiCapabilities } =
      useMetricaAiConfig();
    const aiUnavailable = computed(
      () => aiConfigLoaded.value && !aiCapabilities.value.chat
    );

    const setReplyMode = mode => {
      emit('setReplyMode', mode);
    };
    const handleReplyClick = () => {
      if (props.isReplyRestricted) return;
      setReplyMode(REPLY_EDITOR_MODES.REPLY);
    };
    const handleNoteClick = () => {
      setReplyMode(REPLY_EDITOR_MODES.NOTE);
    };
    const handleModeToggle = () => {
      const newMode =
        props.mode === REPLY_EDITOR_MODES.REPLY
          ? REPLY_EDITOR_MODES.NOTE
          : REPLY_EDITOR_MODES.REPLY;
      setReplyMode(newMode);
    };

    // Metrica AI assist button
    const showMetricaMenu = ref(false);
    const metricaMenuRef = ref(null);
    const metricaLoading = ref(false);

    const metricaMenuItems = [
      {
        key: 'metrica_suggest',
        label: t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.SUGGESTION'),
        icon: 'i-fluent-chat-sparkle-16-regular',
      },
      {
        key: 'metrica_summarize',
        label: t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.SUMMARIZE'),
        icon: 'i-fluent-text-bullet-list-square-sparkle-32-regular',
      },
    ];

    const handleMetricaAction = async key => {
      showMetricaMenu.value = false;
      if (
        metricaLoading.value ||
        !props.conversationId ||
        aiUnavailable.value
      ) {
        return;
      }
      metricaLoading.value = true;
      try {
        const { data } =
          key === 'metrica_suggest'
            ? await MetricaAiAssistAPI.suggestReply(props.conversationId)
            : await MetricaAiAssistAPI.summarize(props.conversationId);
        if (data?.text) {
          emit('executeCopilotAction', 'metrica_inject', data.text);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[MetricaAI] assist error:', error);
        const msg =
          error?.response?.status === 403
            ? t('METRICA_AI_ASSIST.UPGRADE_REQUIRED')
            : t('METRICA_AI_ASSIST.ERROR');
        useAlert(msg);
      } finally {
        metricaLoading.value = false;
      }
    };

    const keyboardEvents = {
      'Alt+KeyP': {
        action: () => handleNoteClick(),
        allowOnFocusedInput: false,
      },
      'Alt+KeyL': {
        action: () => handleReplyClick(),
        allowOnFocusedInput: false,
      },
    };
    useKeyboardEvents(keyboardEvents);

    return {
      handleModeToggle,
      handleReplyClick,
      handleNoteClick,
      REPLY_EDITOR_MODES,
      showMetricaMenu,
      metricaMenuRef,
      metricaLoading,
      metricaMenuItems,
      handleMetricaAction,
      aiUnavailable,
    };
  },
  computed: {
    replyButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.REPLY,
      };
    },
    noteButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.NOTE,
      };
    },
    charLengthClass() {
      return this.charactersRemaining < 0 ? 'text-n-ruby-9' : 'text-n-slate-11';
    },
    characterLengthWarning() {
      return this.charactersRemaining < 0
        ? `${-this.charactersRemaining} ${CHAR_LENGTH_WARNING.NEGATIVE}`
        : `${this.charactersRemaining} ${CHAR_LENGTH_WARNING.UNDER_50}`;
    },
  },
};
</script>

<template>
  <div
    class="flex justify-between gap-2 h-[3.25rem] items-center ltr:pl-3 ltr:pr-2 rtl:pr-3 rtl:pl-2"
  >
    <EditorModeToggle
      :mode="mode"
      :disabled="disabled"
      :is-reply-restricted="isReplyRestricted"
      @toggle-mode="handleModeToggle"
    />
    <div class="flex items-center mx-4 my-0">
      <div v-if="isMessageLengthReachingThreshold" class="text-xs">
        <span :class="charLengthClass">
          {{ characterLengthWarning }}
        </span>
      </div>
    </div>
    <div class="flex items-center gap-2">
      <!-- Prime Insight AI assist button (replaces Chatwoot Captain sparkle) -->
      <div class="relative">
        <NextButton
          ref="metricaMenuRef"
          v-tooltip="
            aiUnavailable ? $t('METRICA_AI_ASSIST.NOT_CONFIGURED_TOOLTIP') : ''
          "
          ghost
          sm
          :disabled="
            disabled || isEditorDisabled || metricaLoading || aiUnavailable
          "
          :icon="metricaLoading ? 'i-lucide-loader-2' : 'i-ph-sparkle-fill'"
          :class="{
            'text-n-brand hover:enabled:!bg-n-blue-3': !showMetricaMenu,
            'text-n-brand bg-n-blue-3': showMetricaMenu,
          }"
          @click="showMetricaMenu = !showMetricaMenu"
        />
        <div
          v-if="showMetricaMenu"
          v-on-click-outside="[
            () => (showMetricaMenu = false),
            { ignore: [metricaMenuRef] },
          ]"
          class="absolute ltr:right-0 rtl:left-0 bottom-full mb-2 z-50 min-w-[13rem] rounded-lg border border-n-strong bg-n-solid-1 shadow-md py-1.5"
        >
          <button
            v-for="item in metricaMenuItems"
            :key="item.key"
            class="flex w-full items-center gap-2 px-3 py-2 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
            @click="handleMetricaAction(item.key)"
          >
            <span class="size-3.5 shrink-0 text-n-brand" :class="[item.icon]" />
            {{ item.label }}
          </button>
        </div>
      </div>
      <NextButton
        ghost
        class="text-n-slate-11"
        sm
        icon="i-lucide-maximize-2"
        @click="$emit('toggleEditorSize')"
      />
    </div>
  </div>
</template>
